const scoreOf = (player) => Number(player.points) || 0;
const colorBalance = (player) => (player.colors || []).reduce(
  (balance, color) => balance + (color === "white" ? 1 : color === "black" ? -1 : 0), 0,
);
const repeatsLastColorTwice = (player, color) => {
  const colors = player.colors || [];
  return colors.length >= 2 && colors.at(-1) === color && colors.at(-2) === color;
};

const ratingOf = (player) => Number(
  player.ratingUsed ?? player.rating_used ?? player.elo,
);

const stableOrderOf = (player) => {
  const value = Number(player.startNumber ?? player.start_number);
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
};

const compareStableId = (first, second) => String(first.id).localeCompare(
  String(second.id), undefined, { numeric: true },
);

export const sortSwissStartingList = (players) => players
  .map((player) => ({ ...player, startingOrder: stableOrderOf(player) }))
  .sort((first, second) => (
    ratingOf(second) - ratingOf(first)
    || first.startingOrder - second.startingOrder
    || compareStableId(first, second)
  ))
  .map((player, index) => ({ ...player, seed: index + 1 }));

const colorCost = (white, black) => (
  Math.abs(colorBalance(white) + 1)
  + Math.abs(colorBalance(black) - 1)
  + (repeatsLastColorTwice(white, "white") ? 20 : 0)
  + (repeatsLastColorTwice(black, "black") ? 20 : 0)
);

const orientPair = (first, second) => {
  const firstWhite = colorCost(first, second);
  const secondWhite = colorCost(second, first);
  if (firstWhite < secondWhite) return [first, second];
  if (secondWhite < firstWhite) return [second, first];
  return compareStableId(first, second) <= 0 ? [first, second] : [second, first];
};

const gameForPair = ([first, second], round, boardIndex, alternateColors = false) => {
  let [white, black] = orientPair(first, second);
  if (alternateColors && boardIndex % 2 === 1) [white, black] = [black, white];
  return {
    id: `swiss-${round}-${boardIndex + 1}`,
    round,
    whitePlayerId: white.id,
    blackPlayerId: black.id,
    result: null,
    resultSource: null,
    played: false,
    completed: false,
    probabilities: null,
    bye: false,
  };
};

const pairingCost = (first, second) => Math.abs(scoreOf(first) - scoreOf(second)) * 1000
  + Math.abs((Number(first.seed) || 0) - (Number(second.seed) || 0));

function pairBacktracking(remaining, previousPairs) {
  if (!remaining.length) return [];
  const first = remaining[0];
  const candidates = remaining.slice(1)
    .filter((candidate) => !previousPairs.has([first.id, candidate.id].sort().join("-")))
    .sort((a, b) => pairingCost(first, a) - pairingCost(first, b));
  for (const candidate of candidates) {
    const rest = remaining.filter((player) => player !== first && player !== candidate);
    const continuation = pairBacktracking(rest, previousPairs);
    if (continuation) return [[first, candidate], ...continuation];
  }
  return null;
}

export function buildSwissRound(players, { round = 1, previousGames = [] } = {}) {
  const seeded = round === 1
    ? sortSwissStartingList(players)
    : players.map((player, index) => ({ ...player, seed: player.seed || index + 1 }));
  let byePlayer = null;
  let eligible = [...seeded];
  if (eligible.length % 2 === 1) {
    if (round === 1) {
      byePlayer = eligible.at(-1);
    } else {
      const byeCandidates = [...eligible].sort((a, b) => (
        Number(Boolean(a.hadBye)) - Number(Boolean(b.hadBye))
        || scoreOf(a) - scoreOf(b)
        || (Number(b.seed) || 0) - (Number(a.seed) || 0)
      ));
      byePlayer = byeCandidates[0];
    }
    eligible = eligible.filter(({ id }) => id !== byePlayer.id);
  }
  if (round === 1) {
    const half = eligible.length / 2;
    const pairs = eligible.slice(0, half).map((player, index) => [player, eligible[half + index]]);
    const games = pairs.map((pair, boardIndex) => gameForPair(pair, round, boardIndex, true));
    if (byePlayer) games.push({
      id: `swiss-${round}-bye-${byePlayer.id}`,
      round,
      whitePlayerId: byePlayer.id,
      blackPlayerId: null,
      result: "bye",
      resultSource: "simulated",
      played: true,
      completed: true,
      probabilities: null,
      bye: true,
    });
    return games;
  }
  const sorted = eligible.sort((a, b) => scoreOf(b) - scoreOf(a) || (Number(a.seed) || 0) - (Number(b.seed) || 0));
  const previousPairs = new Set(previousGames
    .filter((game) => game.whitePlayerId != null && game.blackPlayerId != null)
    .map((game) => [game.whitePlayerId, game.blackPlayerId].sort().join("-")));
  const pairs = pairBacktracking(sorted, previousPairs);
  if (!pairs) throw new Error("No repeat-free Swiss pairing available");
  const games = pairs.map((pair, boardIndex) => gameForPair(pair, round, boardIndex));
  if (byePlayer) games.push({
    id: `swiss-${round}-bye-${byePlayer.id}`,
    round,
    whitePlayerId: byePlayer.id,
    blackPlayerId: null,
    result: "bye",
    resultSource: "simulated",
    played: true,
    completed: true,
    probabilities: null,
    bye: true,
  });
  return games;
}
